import { useDomainMutation } from "@pps/mutations";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cleanPayload } from "@pps/supabase/payload";

export interface LessonCheckinInput {
  lessonId: string;
  lessonDate: string; // YYYY-MM-DD
  latitude?: number;
  longitude?: number;
}

export interface LessonCheckinResult {
  success: true;
  attendance: {
    id: string;
    lesson_id: string;
    student_id: string;
    lesson_date: string;
    status: string;
    check_in_at: string | null;
    check_in_method: string | null;
  };
  distance_meters: number | null;
}

export type LessonCheckinErrorCode =
  | "unauthorized"
  | "invalid_body"
  | "lesson_id_required"
  | "invalid_lesson_date"
  | "invalid_coordinates"
  | "lesson_not_found"
  | "lesson_inactive"
  | "date_does_not_match_lesson_schedule"
  | "checkin_only_allowed_today"
  | "checkin_not_open_yet"
  | "checkin_window_closed"
  | "student_not_found"
  | "student_not_active"
  | "not_enrolled_in_lesson"
  | "too_far_from_club"
  | "checkin_failed"
  | "internal_error"
  | "rate_limited"
  | "method_not_allowed"
  | "network_error";

export class LessonCheckinError extends Error {
  code: LessonCheckinErrorCode;
  distanceMeters?: number;
  maxDistanceMeters?: number;

  constructor(
    code: LessonCheckinErrorCode,
    message?: string,
    extra?: { distanceMeters?: number; maxDistanceMeters?: number }
  ) {
    super(message ?? code);
    this.code = code;
    this.distanceMeters = extra?.distanceMeters;
    this.maxDistanceMeters = extra?.maxDistanceMeters;
  }
}

export async function invokeLessonCheckin(
  input: LessonCheckinInput
): Promise<LessonCheckinResult> {
  const { data, error } = await supabase.functions.invoke<
    LessonCheckinResult | { error: LessonCheckinErrorCode; distance_meters?: number; max_distance_meters?: number }
  >("lesson-checkin", {
    body: cleanPayload({
      lessonId: input.lessonId,
      lessonDate: input.lessonDate,
      latitude: input.latitude,
      longitude: input.longitude,
    }),
  });

  if (error) {
    // Tentar extrair o codigo de erro do payload
    const ctx = (error as { context?: { json?: () => Promise<unknown> } })
      .context;
    if (ctx?.json) {
      try {
        const parsed = (await ctx.json()) as {
          error?: LessonCheckinErrorCode;
          distance_meters?: number;
          max_distance_meters?: number;
        };
        if (parsed?.error) {
          throw new LessonCheckinError(parsed.error, parsed.error, {
            distanceMeters: parsed.distance_meters,
            maxDistanceMeters: parsed.max_distance_meters,
          });
        }
      } catch (parseErr) {
        if (parseErr instanceof LessonCheckinError) throw parseErr;
      }
    }
    throw new LessonCheckinError("network_error", error.message);
  }

  if (!data || !("success" in data)) {
    const code = (data as { error?: LessonCheckinErrorCode } | null)?.error ?? "internal_error";
    throw new LessonCheckinError(code, code);
  }

  return data;
}

export function useLessonCheckin() {
  // M-5b batch 9: envelopa em useDomainMutation com `toast: false` —
  // callers (CheckinButton, QuickCheckinFAB) ja renderizam UX baseada em
  // `LessonCheckinError.code` (mensagens contextuais por codigo). Wrapper
  // adiciona Sentry + invalidates declarativos. mutationFn intacta.
  //
  // O invalidate de `["lesson-attendance", lessonId]` depende de `variables`
  // e nao cabe em `invalidates` estatico — feito no callback `onSuccess`
  // adicional, que roda APOS o handler default (que ja invalida `my-attendance`).
  const queryClient = useQueryClient();
  return useDomainMutation<LessonCheckinResult, LessonCheckinError, LessonCheckinInput>({
    mutationFn: invokeLessonCheckin,
    invalidates: ["my-attendance"],
    toast: false,
    tags: { feature: "public", action: "lesson_checkin" },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["lesson-attendance", variables.lessonId],
      });
    },
  });
}
